import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaCheck, FaTimes } from 'react-icons/fa';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AdminNav from './AdminNav';

const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const Title = styled.h1`
  color: #333;
  margin: 0;
`;

const ActionButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background-color: #C41E3A;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #A01930;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TableHead = styled.thead`
  background-color: #f5f5f5;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #555;
`;

const TableCell = styled.td`
  padding: 1rem;
  color: #333;
`;

const ProductImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  border: none;
  background-color: ${props => props.delete ? '#f8d7da' : '#e2f0ff'};
  color: ${props => props.delete ? '#dc3545' : '#0d6efd'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.delete ? '#f5c2c7' : '#cfe2ff'};
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => props.inStock ? '#d1e7dd' : '#f8d7da'};
  color: ${props => props.inStock ? '#0f5132' : '#842029'};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const PageButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ddd;
  background-color: ${props => props.active ? '#C41E3A' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  cursor: pointer;
  
  &:first-child {
    border-radius: 4px 0 0 4px;
  }
  
  &:last-child {
    border-radius: 0 4px 4px 0;
  }
  
  &:hover {
    background-color: ${props => props.active ? '#A01930' : '#f5f5f5'};
  }
  
  &:disabled {
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const EmptyTitle = styled.h3`
  color: #333;
  margin-bottom: 1rem;
`;

const EmptyText = styled.p`
  color: #666;
  margin-bottom: 2rem;
`;

const InventoryCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InventoryInput = styled.input`
  width: 70px;
  padding: 0.4rem 0.6rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: #C41E3A;
    box-shadow: 0 0 0 2px rgba(196, 30, 58, 0.2);
  }
`;

const InventoryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: none;
  background-color: ${props => props.cancel ? '#f8d7da' : '#d1e7dd'};
  color: ${props => props.cancel ? '#dc3545' : '#0f5132'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.cancel ? '#f5c2c7' : '#badbcc'};
  }
`;

const InventoryDisplay = styled.span`
  font-weight: 500;
  color: ${props => props.quantity <= 0 ? '#dc3545' : props.quantity < 10 ? '#fd7e14' : '#0f5132'};
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [editingInventory, setEditingInventory] = useState(null);
  const [inventoryValue, setInventoryValue] = useState('');
  const productsPerPage = 10;
  
  // Fetch products from Firestore
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsCollection = collection(db, 'products');
        const productsSnapshot = await getDocs(productsCollection);
        const productsList = productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProducts(productsList);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);
  
  // Handle search
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  
  // Filter products based on search query
  const filteredProducts = products.filter(product => 
    product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // Delete product
  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteDoc(doc(db, 'products', id));
        setProducts(products.filter(product => product.id !== id));
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };
  
  // Start editing inventory
  const startEditingInventory = (productId, currentQuantity) => {
    setEditingInventory(productId);
    setInventoryValue(currentQuantity || 0);
  };
  
  // Cancel editing inventory
  const cancelEditingInventory = () => {
    setEditingInventory(null);
    setInventoryValue('');
  };
  
  // Save inventory
  const saveInventory = async (productId) => {
    try {
      const quantity = parseInt(inventoryValue);
      if (isNaN(quantity) || quantity < 0) {
        alert('Please enter a valid quantity (0 or greater)');
        return;
      }
      
      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, {
        quantity: quantity,
        inStock: quantity > 0
      });
      
      setProducts(products.map(product => 
        product.id === productId 
          ? { ...product, quantity: quantity, inStock: quantity > 0 }
          : product
      ));
      
      setEditingInventory(null);
      setInventoryValue('');
    } catch (error) {
      console.error('Error updating inventory:', error);
      alert('Failed to update inventory');
    }
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };
  
  if (loading) {
    return <Container>Loading products...</Container>;
  }
  
  return (
    <Container>
      <Header>
        <Title>Product Management</Title>
        <ActionButton to="/admin/products/add">
          <FaPlus /> Add New Product
        </ActionButton>
      </Header>
      
      <AdminNav />
      
      <SearchContainer>
        <SearchIcon>
          <FaSearch />
        </SearchIcon>
        <SearchInput
          type="text"
          placeholder="Search products..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </SearchContainer>
      
      {filteredProducts.length > 0 ? (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Image</TableHeader>
                <TableHeader>Name</TableHeader>
                <TableHeader>Category</TableHeader>
                <TableHeader>Price</TableHeader>
                <TableHeader>Inventory</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {currentProducts.map(product => (
                <TableRow key={product.id}>
                  <TableCell>
                    <ProductImage 
                      src={product.image || 'https://via.placeholder.com/60x60?text=No+Image'} 
                      alt={product.name} 
                    />
                  </TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.category}</TableCell>
                  <TableCell>{formatCurrency(product.price)}</TableCell>
                  <TableCell>
                    {editingInventory === product.id ? (
                      <InventoryCell>
                        <InventoryInput
                          type="number"
                          min="0"
                          value={inventoryValue}
                          onChange={(e) => setInventoryValue(e.target.value)}
                        />
                        <InventoryButton onClick={() => saveInventory(product.id)}>
                          <FaCheck />
                        </InventoryButton>
                        <InventoryButton cancel onClick={cancelEditingInventory}>
                          <FaTimes />
                        </InventoryButton>
                      </InventoryCell>
                    ) : (
                      <InventoryDisplay 
                        quantity={product.quantity || 0}
                        onClick={() => startEditingInventory(product.id, product.quantity)}
                      >
                        {product.quantity || 0}
                      </InventoryDisplay>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge inStock={product.inStock}>
                      {product.inStock ? 'In Stock' : 'Out of Stock'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <ActionButtons>
                      <IconButton as={Link} to={`/admin/products/edit/${product.id}`}>
                        <FaEdit />
                      </IconButton>
                      <IconButton delete onClick={() => handleDeleteProduct(product.id)}>
                        <FaTrash />
                      </IconButton>
                    </ActionButtons>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
          
          {totalPages > 1 && (
            <Pagination>
              <PageButton 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
              >
                &lt;
              </PageButton>
              
              {[...Array(totalPages)].map((_, index) => (
                <PageButton
                  key={index}
                  active={currentPage === index + 1}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </PageButton>
              ))}
              
              <PageButton 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
              >
                &gt;
              </PageButton>
            </Pagination>
          )}
        </>
      ) : (
        <EmptyState>
          <EmptyTitle>No products found</EmptyTitle>
          <EmptyText>
            {searchQuery 
              ? `No products match your search for "${searchQuery}"`
              : "You haven't added any products yet"}
          </EmptyText>
          <ActionButton to="/admin/products/add">
            <FaPlus /> Add Your First Product
          </ActionButton>
        </EmptyState>
      )}
    </Container>
  );
};

export default ProductManagement;
